import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  QButton,
  QCenter,
  QCloseButton,
  QIcon,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
  useCurrentUser,
} from '@qualio/ui-components';
import { useNavigate } from 'react-router-dom';

import templatesApi from '../../api/templates.api';
import { QualityEventCreate, QualityEventCreateWithTitleRestriction, EventTemplateField } from '../../types';
import EventForm from './components/EventForm';
import { useCreateEvent } from '../../hooks/useCreateEvent';
import { CreateEvent } from '../../displayStrings';
import { useEventTypes, useTags, useUsers } from '../../hooks';
import { useEventMetadata } from '../../hooks/useEventMetadata';
import { useProducts } from '../../hooks/useProducts';
import { useRootCauses } from '../../hooks/useRootCauses';
import { filterTags } from '../../utils/eventUtils';

type EventNewModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const EventNewModal: React.FC<EventNewModalProps> = ({ isOpen, setIsOpen }) => {
  const { t } = useTranslation();
  const { companyId, userId } = useCurrentUser();
  const navigate = useNavigate();

  const eventTypesQueryResult = useEventTypes();
  const eventMetadataQueryResult = useEventMetadata();
  const tagsQueryResult = useTags();
  const productsList = useProducts();
  const rootCausesList = useRootCauses();
  const { data: usersData } = useUsers();
  const filteredTagsData = filterTags(userId, usersData || [], tagsQueryResult.data || []);
  const { mutateAsync: createEventMutate, isLoading: isCreateEventLoading } = useCreateEvent();

  const [optionalFields, setOptionalFields] = useState<EventTemplateField[]>([
    { attribute_name: 'product', mandatory: false },
    { attribute_name: 'root_cause', mandatory: false },
    { attribute_name: 'risk', mandatory: true },
  ]);

  const formMethods = useForm<QualityEventCreateWithTitleRestriction>({
    mode: 'onSubmit',
    resolver: zodResolver(QualityEventCreateWithTitleRestriction),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isSubmitSuccessful },
    watch,
  } = formMethods;

  const eventType = watch('workflow_id');

  useEffect(() => {
    if (eventType) {
      const getOptionalFields = async () => {
        const templateDetails = await templatesApi.getEventTemplateDetails(companyId, parseInt(eventType, 10));
        if (!templateDetails) {
          return;
        }
        setOptionalFields(templateDetails.fields);
      };
      getOptionalFields().catch(console.error);
      return;
    }
  }, [eventType, companyId]);

  const formHasErrors = !!Object.keys(errors).length;

  /**
   * Normally against using an effect to watch state, but this is the react hook form official
   * guidance for resetting on submit:
   * https://react-hook-form.com/docs/useform/reset
   */
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [isSubmitSuccessful, reset]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const onSubmit = useCallback(
    (payload: QualityEventCreate) => {
      createEventMutate(payload, {
        onSuccess: ({ id }) => {
          navigate(`/events/${id}`);
        },
      });
    },
    [createEventMutate, navigate],
  );

  return (
    <QModal isOpen={isOpen} data-cy={'event-new-modal'} onClose={handleClose} size="xl">
      <QModalHeader>
        <QText>{CreateEvent}</QText>
        <QCloseButton onClick={handleClose} data-metrics="create-event-close-modal-button" />
      </QModalHeader>
      <QModalBody>
        <QStack direction="column" color="gray.700" spacing={4}>
          <QStack direction="row" mb={2}>
            <QCenter>
              <QIcon iconName="Info" />
            </QCenter>
            <QCenter>
              <QText fontSize="sm">{t('events.create.provideRequiredInformationToCreateAnEvent')}</QText>
            </QCenter>
          </QStack>
          <EventForm
            formMethods={formMethods}
            onSubmit={onSubmit}
            data={{
              eventTypes: eventTypesQueryResult.data || [],
              eventTags: filteredTagsData,
              probabilities: eventMetadataQueryResult.data?.probabilities || [],
              severities: eventMetadataQueryResult.data?.severities || [],
              risks: eventMetadataQueryResult.data?.risks || [],
              users: usersData || [],
              products: productsList.data || [],
              rootCauses: rootCausesList.data || [],
            }}
            optionalFields={optionalFields}
          />
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton
          onClick={handleClose}
          variant="outline"
          data-metrics="create-event-cancel-button"
          data-cy="event-modal-cancel-button"
          isDisabled={isCreateEventLoading}
        >
          {t('common.actions.cancel')}
        </QButton>
        <QButton
          onClick={handleSubmit(onSubmit)}
          isDisabled={formHasErrors}
          isLoading={isCreateEventLoading}
          data-metrics="create-event-button"
          data-cy="create-event-button"
          aria-label="create-event-button"
        >
          {t('events.actions.createEvent')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};

export default EventNewModal;
